import React from "react";
import { connect } from "react-redux";

// In-house replacement for the abandoned `redux-little-router` (14.2.0), which fails to
// evaluate under React 18 — its React-15-era `recompose` dependency throws at module-load
// ("Super expression must either be null or a function"), crashing the whole global.js bundle.
//
// This reproduces ONLY the surface the tariff-survey-hub uses — routerForBrowser /
// initializeCurrentLocation / Link / Fragment — for the app's fixed `/:section/:tab` routes
// under a basename. It is URL-state-sync, not routing: the hub renders every tab in one page;
// the "router" just mirrors the section/tab from the URL into the redux store and back.
//
// The redux-little-router shape the app consumes (see tariff_survey_hub/{app,middlewares,
// reducers,actions/ajax}.js + routes.js):
//   state.router = { pathname (post-basename), basename, params: {section, tab}, query, search }
// and the LOCATION_CHANGED action type string "ROUTER_LOCATION_CHANGED".

export const LOCATION_CHANGED = "ROUTER_LOCATION_CHANGED";

const parseQuery = (search) => {
  const query = {};
  (search || "").replace(/^\?/, "").split("&").filter(Boolean).forEach((pair) => {
    const idx = pair.indexOf("=");
    const key = idx === -1 ? pair : pair.slice(0, idx);
    const val = idx === -1 ? "" : pair.slice(idx + 1);
    query[decodeURIComponent(key)] = decodeURIComponent(val);
  });
  return query;
};

// Build the redux-little-router-shaped location object. `pathname` is post-basename
// (ajax.js fetches `basename + pathname + ".json"`); params are the two path segments.
const buildLocation = (fullPathname, basename, search) => {
  let pathname = fullPathname || "/";
  if (basename && pathname.indexOf(basename) === 0) {
    pathname = pathname.slice(basename.length) || "/";
  }
  const segments = pathname.split("/").filter(Boolean);
  return {
    pathname,
    basename: basename || "",
    params: { section: segments[0], tab: segments[1] },
    query: parseQuery(search),
    search: search || "",
  };
};

const locationChanged = (location) => ({ type: LOCATION_CHANGED, payload: location });

// Re-dispatch the seeded location so the section/tab middleware + saga run for the first
// render (the app calls this with store.getState().router right after store creation).
export const initializeCurrentLocation = (location) => locationChanged(location);

export const routerForBrowser = ({ basename = "" } = {}) => {
  const currentBrowserLocation = () =>
    buildLocation(window.location.pathname, basename, window.location.search);

  const initialLocation = currentBrowserLocation();

  const reducer = (state = initialLocation, action) =>
    (action.type === LOCATION_CHANGED ? action.payload : state);

  // Link dispatches LOCATION_CHANGED itself (after pushState), so this is a passthrough —
  // it exists only because app.js applies it in the middleware chain.
  const middleware = () => (next) => (action) => next(action);

  // Seed router state via the reducer default; wire browser back/forward to the store.
  const enhancer = (createStoreFn) => (rootReducer, preloadedState, innerEnhancer) => {
    const store = createStoreFn(rootReducer, preloadedState, innerEnhancer);
    window.addEventListener("popstate", () => {
      store.dispatch(locationChanged(currentBrowserLocation()));
    });
    return store;
  };

  return { reducer, middleware, enhancer };
};

// <Link href="/section/tab"> — renders an <a>, navigates via pushState + LOCATION_CHANGED.
// `activeProps` (e.g. { className: "active ..." }) is merged when the link matches the route.
class LinkComponent extends React.Component {
  handleClick = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
      this.props.target
    ) {
      return;
    }
    event.preventDefault();
    const basename = (this.props.router && this.props.router.basename) || "";
    const fullPath = basename + this.props.href;
    window.history.pushState(null, "", fullPath);
    this.props.dispatch(locationChanged(buildLocation(fullPath, basename, "")));
  };

  render() {
    const {
      // eslint-disable-next-line no-unused-vars
      href, children, className, activeProps, router, dispatch, persistQuery, ...rest
    } = this.props;
    const basename = (router && router.basename) || "";
    const current = (router && router.pathname) || "";
    const isActive = current === href || current.indexOf(href + "/") === 0;
    const merged = isActive && activeProps ? activeProps : {};
    const cssClass = [className, merged.className].filter(Boolean).join(" ") || undefined;
    return (
      <a href={basename + href} onClick={this.handleClick} className={cssClass} {...rest}>
        {children}
      </a>
    );
  }
}

export const Link = connect((state) => ({ router: state.router }))(LinkComponent);

// <Fragment forRoute="/segment"> — renders children only when the route segment at this
// nesting depth matches. Depth 0 matches params.section; depth 1 matches params.tab (tab
// Fragments are rendered inside their section Fragment — see root.js / *_section.js).
const RouteDepthContext = React.createContext(0);

class FragmentComponent extends React.Component {
  render() {
    const { forRoute, router, children } = this.props;
    const target = (forRoute || "").replace(/^\//, "");
    return (
      <RouteDepthContext.Consumer>
        {(depth) => {
          const params = (router && router.params) || {};
          const current = depth === 0 ? params.section : params.tab;
          if (current !== target) {
            return null;
          }
          return (
            <RouteDepthContext.Provider value={depth + 1}>
              {children}
            </RouteDepthContext.Provider>
          );
        }}
      </RouteDepthContext.Consumer>
    );
  }
}

export const Fragment = connect((state) => ({ router: state.router }))(FragmentComponent);
